import { useCallback, useEffect, useMemo, useState } from 'react';

import { GET_DEAL_GENERAL_INFO_QUERY } from 'features/deals/dealCards/gql/dealDetailsQueries';
import { Criteria } from 'features/pages/marketplace/TradeCarveManager/CreateCarveForm';
import {
  convertFiltersToCriteria,
  criteriaToFilters,
} from 'features/pages/marketplace/TradeCarveManager/criteriaToFilterMapper';
import _ from 'lodash';

import { ApolloCache, useLazyQuery, useMutation } from '@apollo/client';

import { CreateDealCarve, CreateDealCarveVariables } from 'mutation/__generated__/createDealCarve';
import { DeleteDealCarve, DeleteDealCarveVariables } from 'mutation/__generated__/deleteDealCarve';
import { UpdateDealCarve, UpdateDealCarveVariables } from 'mutation/__generated__/updateDealCarve';
import { CREATE_DEAL_CARVE } from 'mutation/createDealCarve';
import { DELETE_DEAL_CARVE } from 'mutation/deleteDealCarve';
import { UPDATE_DEAL_CARVE } from 'mutation/updateDealCarve';

import { GetCarves, GetCarvesVariables } from 'query/__generated__/getCarves';
import { GET_CARVES } from 'query/getCarves';

import { CarveSpecification } from './types';

import { GetCarve, GetCarveVariables } from '../CarveDiffTable/__generated__/fragments';
import { GET_CARVE } from '../CarveDiffTable/fragments';

import { CarveDetailsCard } from './__generated__/fragments';

type Carves = NonNullable<
  NonNullable<Extract<CarveDetailsCard, { __typename: 'CarveDetailsCard' }>>['carves']
>[number];
function patchGetCarvesCache(
  cache: ApolloCache<object>,
  dealId: string,
  updater: (existing: GetCarves) => GetCarves | undefined,
) {
  try {
    cache.updateQuery<GetCarves, GetCarvesVariables>(
      { query: GET_CARVES, variables: { deal_id: dealId } },
      (existing) => {
        if (!existing) {
          return existing;
        }
        return updater(existing);
      },
    );
  } catch (error) {
    console.warn('[useCarveFunctions] GetCarves cache update skipped', {
      dealId,
      error: error instanceof Error ? error.message : error,
    });
  }
}

function removeCarveAfterDelete(cache: ApolloCache<object>, dealId: string, carveId: string) {
  patchGetCarvesCache(cache, dealId, (data) => {
    if (!data.deal?.carves) {
      return data;
    }
    return {
      ...data,
      deal: {
        ...data.deal,
        carves: data.deal.carves.filter((c) => c && c.id !== carveId),
      },
    };
  });
}

const useCarveFunctions = (dealId: string) => {
  const refetchCarvesQueries = useMemo(
    () => [
      { query: GET_CARVES, variables: { deal_id: dealId } },
      { query: GET_DEAL_GENERAL_INFO_QUERY, variables: { id: dealId } },
    ],
    [dealId],
  );

  const [createDealCarve, { loading: createLoading, called: createCalled }] = useMutation<
    CreateDealCarve,
    CreateDealCarveVariables
  >(CREATE_DEAL_CARVE, {
    refetchQueries: refetchCarvesQueries,
  });

  const [updateDealCarve] = useMutation<UpdateDealCarve, UpdateDealCarveVariables>(
    UPDATE_DEAL_CARVE,
    {
      refetchQueries: refetchCarvesQueries,
    },
  );

  const [deleteDealCarve, { loading: deleteLoading, called: deleteCalled }] = useMutation<
    DeleteDealCarve,
    DeleteDealCarveVariables
  >(DELETE_DEAL_CARVE, {
    refetchQueries: refetchCarvesQueries,
    update(cache, _result, { variables }) {
      if (!variables?.id) {
        return;
      }
      removeCarveAfterDelete(cache, dealId, String(variables.id));
    },
  });

  return {
    createDealCarve,
    createLoading,
    createCalled,
    updateDealCarve,
    deleteDealCarve,
    deleteLoading,
    deleteCalled,
  };
};

type UseCarveFunctions = ReturnType<typeof useCarveFunctions>;

const useCarveSpecs = (allCarves: Carves[]) => {
  // Offer carve is a special carve that is not editable. We show a default card for it
  // This logic will break if the user saves a carve with the name 'Offer'
  const offerCarve = _.find(allCarves, (carve) => carve.name === 'Offer');
  const carves = useMemo(
    () => allCarves.filter((carve) => carve.id !== offerCarve?.id),
    [offerCarve, allCarves],
  );

  const [carveSpecifications, setCarveSpecifications] = useState<CarveSpecification[]>([]);
  useEffect(() => {
    const carveIdsFromProps = carves.map((carve) => carve.id);
    setCarveSpecifications((currentCarveSpecs) =>
      currentCarveSpecs.filter((spec) => !carveIdsFromProps.includes(spec.id)),
    );
  }, [carves]);

  const [updatingCarves, setUpdatingCarves] = useState<string[]>([]);
  const [deletingCarves, setDeletingCarves] = useState<string[]>([]);

  useEffect(() => {
    const idsOnServer = new Set((carves || []).map((c) => c.id));
    setDeletingCarves((prev) => prev.filter((id) => idsOnServer.has(id)));
  }, [carves]);

  const serverCarves = useMemo(
    () =>
      (carves || []).map((carve) => ({
        id: carve.id,
        name: carve.name,
        criteria: convertFiltersToCriteria(carve.carve || []),
        performanceSummary: carve.carve_summary,
        isDraft: false,
        isUpdating: updatingCarves.includes(carve.id),
        isDeleted: deletingCarves.includes(carve.id),
      })),
    [carves, updatingCarves, deletingCarves],
  );

  const updatingCarveSpecifications = useMemo(
    () =>
      carveSpecifications.map((carve) => ({
        ...carve,
        isUpdating: updatingCarves.includes(carve.id),
        isDeleted: deletingCarves.includes(carve.id),
      })),
    [carveSpecifications, updatingCarves, deletingCarves],
  );

  const allCarveSpecs = useMemo(
    () => _.uniqBy([...updatingCarveSpecifications, ...serverCarves], 'id'),
    [updatingCarveSpecifications, serverCarves],
  );

  const [fetchCarveData] = useLazyQuery<GetCarve, GetCarveVariables>(GET_CARVE);

  const updateCarveState = (id: string, updateData: Partial<CarveSpecification>) => {
    setCarveSpecifications((carves) =>
      carves.map((carve) => {
        if (carve.id === id) {
          return {
            ...carve,
            ...updateData,
          };
        }
        return carve;
      }),
    );
  };
  const updatePerformanceSummary = useCallback(
    async (dealId: string, id: string, criteria: Criteria[]) => {
      const response = await fetchCarveData({
        variables: {
          id: dealId,
          filters: criteriaToFilters(criteria),
        },
      });

      const carveSummary = response.data?.deal?.performance_summary;
      if (carveSummary) {
        updateCarveState(id, {
          criteria: criteria,
          performanceSummary: carveSummary,
          isUpdating: false,
        });
      }
    },
    [fetchCarveData],
  );

  const createDraftCarve = useCallback(
    async (dealId: string, id: string, name: string, criteria: Criteria[]) => {
      const newCarveSpec = {
        id: id,
        name: name,
        criteria: criteria,
        performanceSummary: null,
        isDraft: true,
        isUpdating: true,
        isDeleted: false,
      };
      setCarveSpecifications((carves) => [newCarveSpec, ...carves]);
      await updatePerformanceSummary(dealId, id, criteria);
    },
    [updatePerformanceSummary],
  );

  const handleCarveUpdateLogic = useCallback(
    (carveSpec: CarveSpecification, callback: () => Promise<unknown>) => {
      setUpdatingCarves((prevCarves) => [...prevCarves, carveSpec.id]);
      callback().then(() => {
        setUpdatingCarves((prevCarves) => prevCarves.filter((id) => id !== carveSpec.id));
      });
    },
    [],
  );

  const completeCarveDelete = useCallback((id: string) => {
    setCarveSpecifications((specs) => specs.filter((s) => s.id !== id));
  }, []);

  return {
    allCarveSpecs,
    createDraftCarve,
    updateCarveState,
    handleCarveUpdateLogic,
    completeCarveDelete,
    setDeletingCarves,
    updatePerformanceSummary,
  };
};

export type { UseCarveFunctions };

export { useCarveFunctions, useCarveSpecs };
